import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Map, { Marker, Popup, NavigationControl, FullscreenControl, ScaleControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

// Humanitarian-focused disaster vector icons using SVG with enhanced professional styling
const createDisasterIcon = (disasterType, severity = 'medium') => {
  // Enhanced color palette for humanitarian context with better contrast
  const colors = {
    high: '#B91C1C',      // Critical red - severe humanitarian impact
    medium: '#D97706',    // Warning orange - moderate humanitarian impact
    low: '#059669'        // Safe green - limited humanitarian impact
  };

  const color = colors[severity];
  const size = severity === 'high' ? 44 : severity === 'medium' ? 36 : 28;

  // Enhanced SVG icons with professional humanitarian symbology
  const svgIcons = {
    'Flood': `<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1">
      <path d="M3 15c0-1.5 3-2.5 9-2.5s9 1 9 2.5-3 2.5-9 2.5-9-1-9-2.5z" opacity="0.7"/>
      <path d="M3 12c0-1.5 3-2.5 9-2.5s9 1 9 2.5-3 2.5-9 2.5-9-1-9-2.5z" opacity="0.8"/>
      <path d="M3 9c0-1.5 3-2.5 9-2.5s9 1 9 2.5-3 2.5-9 2.5-9-1-9-2.5z"/>
      <circle cx="6" cy="8" r="1" opacity="0.6"/>
      <circle cx="18" cy="13" r="1" opacity="0.6"/>
    </svg>`,
    'Storm': `<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5">
      <path d="M6 16.326A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 .5 8.973" fill="none"/>
      <path d="M11 13l-2 4h3l-2 4" fill="none" stroke-width="2"/>
      <path d="M16 11l-1.5 3h2l-1.5 3" fill="none" stroke-width="1.5"/>
      <circle cx="9" cy="10" r="1" opacity="0.7"/>
      <circle cx="15" cy="14" r="1" opacity="0.7"/>
    </svg>`,
    'Earthquake': `<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2">
      <path d="M2 12h2l2-6 4 12 4-9 2 3h2" fill="none" stroke-width="2.5"/>
      <path d="M3 8l18 8" opacity="0.3" stroke-width="1"/>
      <path d="M3 16l18-8" opacity="0.3" stroke-width="1"/>
      <circle cx="12" cy="12" r="2" opacity="0.8"/>
    </svg>`,
    'Drought': `<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2">
      <circle cx="12" cy="12" r="9" fill="none"/>
      <path d="M8 12c4-4 8 0 8 0" opacity="0.6"/>
      <path d="M8 8c4 4 8 0 8 0" opacity="0.4"/>
      <path d="M8 16c4-4 8 0 8 0" opacity="0.4"/>
      <circle cx="12" cy="12" r="1"/>
    </svg>`,
    'Epidemic': `<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5">
      <circle cx="12" cy="12" r="8" fill="none" stroke-dasharray="2,2" opacity="0.6"/>
      <circle cx="12" cy="12" r="4" fill="none"/>
      <circle cx="8" cy="8" r="1.5"/>
      <circle cx="16" cy="8" r="1.5"/>
      <circle cx="8" cy="16" r="1.5"/>
      <circle cx="16" cy="16" r="1.5"/>
      <path d="M12 8v8m-4-4h8" stroke-width="1" opacity="0.7"/>
    </svg>`,
    'Fire': `<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5">
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
      <path d="M12 8c1-2 2-3 3-4" fill="none" opacity="0.6"/>
      <path d="M10 10c0.5-1.5 1-2.5 1.5-3.5" fill="none" opacity="0.6"/>
    </svg>`,
    'Wildfire': `<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5">
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z"/>
      <path d="M7 20h10M9 18h6M11 16h2" stroke-width="1" opacity="0.5"/>
      <circle cx="10" cy="8" r="1" opacity="0.7"/>
      <circle cx="14" cy="10" r="1" opacity="0.7"/>
    </svg>`,
    'Volcanic activity': `<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5">
      <path d="M12 2L6 20h12L12 2z"/>
      <path d="M10 14h4M9 16h6M8 18h8" stroke-width="1" opacity="0.6"/>
      <circle cx="12" cy="8" r="1.5"/>
      <circle cx="10" cy="5" r="0.5" opacity="0.7"/>
      <circle cx="14" cy="6" r="0.5" opacity="0.7"/>
      <circle cx="11" cy="3" r="0.5" opacity="0.5"/>
    </svg>`,
    'Mass movement (wet)': `<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2">
      <path d="M4 8c4-4 8 0 12 4-4 4-8 8-12 12" fill="none"/>
      <path d="M6 6l2 2M10 10l2 2M14 14l2 2" opacity="0.7"/>
      <circle cx="8" cy="8" r="1" opacity="0.6"/>
      <circle cx="12" cy="12" r="1" opacity="0.6"/>
      <circle cx="16" cy="16" r="1" opacity="0.6"/>
    </svg>`,
    'Extreme temperature': `<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2">
      <path d="M14 4v10.54a4 4 0 1 1-4 0V4a2 2 0 0 1 4 0z"/>
      <path d="M12 16a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"/>
      <path d="M12 6h2M12 8h1.5M12 10h2M12 12h1.5" stroke-width="1" opacity="0.7"/>
    </svg>`,
    'Landslide': `<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2">
      <path d="M4 20L12 4l8 16H4z" fill="none"/>
      <path d="M8 16c2-2 4-1 6 0" opacity="0.7"/>
      <circle cx="10" cy="12" r="1" opacity="0.6"/>
      <circle cx="14" cy="14" r="1" opacity="0.6"/>
      <circle cx="12" cy="10" r="0.5" opacity="0.5"/>
    </svg>`,
    'Cyclone': `<svg viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5">
      <circle cx="12" cy="12" r="9" fill="none" opacity="0.3"/>
      <path d="M12 3A9 9 0 0 1 21 12A9 9 0 0 1 12 21A9 9 0 0 1 3 12A9 9 0 0 1 12 3" fill="none" stroke-dasharray="1,1"/>
      <circle cx="12" cy="12" r="3" fill="none"/>
      <path d="M15 9c1 1 1 3 0 4-1 1-3 1-4 0-1-1-1-3 0-4 1-1 3-1 4 0z"/>
    </svg>`
  };

  const iconSvg = svgIcons[disasterType] || svgIcons['Earthquake']; // Default fallback

  // Create professional humanitarian marker with enhanced styling
  const markerHTML = `
    <div style="
      width: ${size}px;
      height: ${size}px;
      background: linear-gradient(135deg, ${color}ee, ${color});
      border: 3px solid rgba(255,255,255,0.9);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow:
        0 4px 12px rgba(0,0,0,0.15),
        0 2px 4px rgba(0,0,0,0.1),
        inset 0 1px 0 rgba(255,255,255,0.3);
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
    "
    onmouseover="
      this.style.transform='scale(1.15) translateY(-2px)';
      this.style.boxShadow='0 8px 25px rgba(0,0,0,0.25), 0 4px 10px rgba(0,0,0,0.15), inset 0 1px 0 rgba(255,255,255,0.4)';
      this.style.borderColor='rgba(255,255,255,1)';
    "
    onmouseout="
      this.style.transform='scale(1) translateY(0px)';
      this.style.boxShadow='0 4px 12px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.3)';
      this.style.borderColor='rgba(255,255,255,0.9)';
    ">
      <div style="
        width: ${size * 0.65}px;
        height: ${size * 0.65}px;
        color: white;
        filter: drop-shadow(0 1px 2px rgba(0,0,0,0.3));
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        ${iconSvg}
      </div>
      ${severity === 'high' ? `
        <div style="
          position: absolute;
          top: -2px;
          right: -2px;
          width: 12px;
          height: 12px;
          background: #EF4444;
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        "></div>
      ` : ''}
    </div>
  `;

  // Create DOM element for the marker
  const el = document.createElement('div');
  el.innerHTML = markerHTML;
  return el;
};

const HistoricalTimelineAnalysisMapbox = () => {
  const [disasters, setDisasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(2020);
  const [selectedDisasterType, setSelectedDisasterType] = useState('All');
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(null);
  const [yearRange] = useState({ min: 2000, max: 2024 });
  const [popupInfo, setPopupInfo] = useState(null);
  const [viewport, setViewport] = useState({
    latitude: 20,
    longitude: 0,
    zoom: 2
  });

  const mapRef = useRef();

  useEffect(() => {
    fetchDisasterData();
  }, []);

  // Auto-play functionality
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setSelectedYear(prev => {
          if (prev >= yearRange.max) {
            setIsPlaying(false);
            return yearRange.min;
          }
          return prev + 1;
        });
      }, 1500); // Change year every 1.5 seconds
    }
    return () => clearInterval(interval);
  }, [isPlaying, yearRange]);

  const fetchDisasterData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use different API endpoints for development vs production
      const isDevelopment = process.env.NODE_ENV === 'development';
      const apiUrl = isDevelopment
        ? '/api/emdat/timeline?startYear=2000&endYear=2024&limit=2000'  // Node.js server (proxied)
        : '/api/emdat?endpoint=timeline&startYear=2000&endYear=2024&limit=2000';  // Vercel serverless

      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error('Failed to fetch disaster timeline data');

      const data = await response.json();

      // Process data - it already has real coordinates and severity levels
      const processedData = data.map(disaster => ({
        ...disaster,
        id: disaster.dis_no || `${disaster.start_year}-${disaster.disaster_type}-${Math.random()}`
      }));

      setDisasters(processedData);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching disaster data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter disasters by year and type
  const filteredDisasters = useMemo(() => {
    return disasters.filter(disaster => {
      const yearMatch = disaster.start_year === selectedYear;
      const typeMatch = selectedDisasterType === 'All' || disaster.disaster_type === selectedDisasterType;
      return yearMatch && typeMatch;
    });
  }, [disasters, selectedYear, selectedDisasterType]);

  // Get unique disaster types for filter
  const disasterTypes = useMemo(() => {
    const types = [...new Set(disasters.map(d => d.disaster_type))];
    return ['All', ...types.sort()];
  }, [disasters]);

  // Calculate statistics for current year
  const yearStats = useMemo(() => {
    const yearData = disasters.filter(d => d.start_year === selectedYear);
    const typeCount = {};

    yearData.forEach(d => {
      typeCount[d.disaster_type] = (typeCount[d.disaster_type] || 0) + 1;
    });

    const mostCommonType = Object.entries(typeCount)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || 'None';

    // Calculate economic impact
    const totalEconomicDamage = yearData.reduce((sum, d) => sum + (d.total_damage_adj || d.total_damage || 0), 0);
    const totalReconstructionCost = yearData.reduce((sum, d) => sum + (d.reconstruction_costs_adj || d.reconstruction_costs || 0), 0);

    // Calculate response statistics
    const disastersWithOfdaResponse = yearData.filter(d => d.ofda_response === 'Yes').length;
    const disastersWithAppeal = yearData.filter(d => d.appeal === 'Yes').length;
    const disastersWithDeclaration = yearData.filter(d => d.declaration === 'Yes').length;

    // Calculate duration statistics
    const disastersWithDuration = yearData.filter(d => d.duration_months > 0);
    const avgDuration = disastersWithDuration.length > 0
      ? disastersWithDuration.reduce((sum, d) => sum + d.duration_months, 0) / disastersWithDuration.length
      : 0;

    return {
      totalDisasters: yearData.length,
      totalDeaths: yearData.reduce((sum, d) => sum + (d.total_deaths || 0), 0),
      totalAffected: yearData.reduce((sum, d) => sum + (d.total_affected || 0), 0),
      totalInjured: yearData.reduce((sum, d) => sum + (d.no_injured || 0), 0),
      totalHomeless: yearData.reduce((sum, d) => sum + (d.no_homeless || 0), 0),
      totalEconomicDamage,
      totalReconstructionCost,
      mostCommon: mostCommonType,
      responseRate: yearData.length > 0 ? Math.round((disastersWithOfdaResponse / yearData.length) * 100) : 0,
      appealRate: yearData.length > 0 ? Math.round((disastersWithAppeal / yearData.length) * 100) : 0,
      declarationRate: yearData.length > 0 ? Math.round((disastersWithDeclaration / yearData.length) * 100) : 0,
      avgDuration: Math.round(avgDuration * 10) / 10,
      deadliestDisaster: yearData.reduce((max, d) => (d.total_deaths || 0) > (max.total_deaths || 0) ? d : max, {}),
      mostAffectedDisaster: yearData.reduce((max, d) => (d.total_affected || 0) > (max.total_affected || 0) ? d : max, {}),
      costliestDisaster: yearData.reduce((max, d) => {
        const damage = d.total_damage_adj || d.total_damage || 0;
        const maxDamage = max.total_damage_adj || max.total_damage || 0;
        return damage > maxDamage ? d : max;
      }, {})
    };
  }, [disasters, selectedYear]);

  const onMarkerClick = useCallback((evt, disaster) => {
    evt.originalEvent.stopPropagation();
    setPopupInfo({
      ...disaster,
      latitude: disaster.latitude,
      longitude: disaster.longitude
    });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700 mx-auto"></div>
          <p className="mt-4 opacity-60">Loading historical disaster timeline...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-minimal border-red-200 bg-red-50">
        <h2 className="text-xl font-serif font-light text-red-800 mb-4">Error Loading Timeline</h2>
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchDisasterData}
          className="mt-4 px-4 py-2 bg-red-600 text-white hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-light mb-4">Historical Disaster Timeline</h1>
        <p className="text-base opacity-80 leading-relaxed">
          Explore global disaster patterns through time using the timeline controls below.
          Enhanced with professional Mapbox visualization and vector disaster icons.
        </p>
      </div>

      {/* Timeline Controls */}
      <div className="card-minimal mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-serif font-light">Timeline Controls</h3>
          <div className="text-2xl font-mono font-light text-slate-700">
            {selectedYear}
          </div>
        </div>

        {/* Year Slider */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <span className="text-sm font-mono">{yearRange.min}</span>
            <div className="flex-1 relative">
              <input
                type="range"
                min={yearRange.min}
                max={yearRange.max}
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="w-full h-2 bg-stone-200 outline-none appearance-none slider"
                style={{
                  background: `linear-gradient(to right, #64748b 0%, #64748b ${((selectedYear - yearRange.min) / (yearRange.max - yearRange.min)) * 100}%, #e2e8f0 ${((selectedYear - yearRange.min) / (yearRange.max - yearRange.min)) * 100}%, #e2e8f0 100%)`
                }}
              />
            </div>
            <span className="text-sm font-mono">{yearRange.max}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white hover:bg-slate-700 transition-colors"
          >
            {isPlaying ? '⏸️' : '▶️'}
            {isPlaying ? 'Pause' : 'Play'}
          </button>

          <button
            onClick={() => setSelectedYear(yearRange.min)}
            className="px-3 py-2 border border-stone-300 hover:bg-stone-50 transition-colors text-sm"
          >
            ⏮️ Start
          </button>

          <button
            onClick={() => setSelectedYear(prev => Math.max(yearRange.min, prev - 1))}
            className="px-3 py-2 border border-stone-300 hover:bg-stone-50 transition-colors text-sm"
          >
            ⏪ -1
          </button>

          <button
            onClick={() => setSelectedYear(prev => Math.min(yearRange.max, prev + 1))}
            className="px-3 py-2 border border-stone-300 hover:bg-stone-50 transition-colors text-sm"
          >
            +1 ⏩
          </button>

          <div className="flex items-center gap-2 ml-4">
            <label className="text-sm font-mono">Filter:</label>
            <select
              value={selectedDisasterType}
              onChange={(e) => setSelectedDisasterType(e.target.value)}
              className="px-3 py-1 border border-stone-300 text-sm bg-white focus:outline-none focus:border-slate-500"
            >
              {disasterTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Enhanced Year Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Basic Impact Stats */}
        <div className="card-minimal text-center">
          <div className="text-2xl font-mono font-light text-slate-700">{yearStats.totalDisasters}</div>
          <div className="text-sm opacity-60 mt-1">Total Disasters</div>
        </div>
        <div className="card-minimal text-center">
          <div className="text-2xl font-mono font-light text-red-600">{yearStats.totalDeaths.toLocaleString()}</div>
          <div className="text-sm opacity-60 mt-1">Deaths</div>
          {yearStats.totalInjured > 0 && (
            <div className="text-xs text-orange-500 mt-1">{yearStats.totalInjured.toLocaleString()} injured</div>
          )}
        </div>
        <div className="card-minimal text-center">
          <div className="text-2xl font-mono font-light text-amber-600">{yearStats.totalAffected.toLocaleString()}</div>
          <div className="text-sm opacity-60 mt-1">Total Affected</div>
          {yearStats.totalHomeless > 0 && (
            <div className="text-xs text-red-500 mt-1">{yearStats.totalHomeless.toLocaleString()} homeless</div>
          )}
        </div>
        <div className="card-minimal text-center">
          <div className="text-lg font-sans font-light text-slate-700">{yearStats.mostCommon}</div>
          <div className="text-sm opacity-60 mt-1">Most Common Type</div>
        </div>
      </div>

      {/* Economic Impact & Response Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="card-minimal text-center">
          <div className="text-xl font-mono font-light text-green-700">
            ${yearStats.totalEconomicDamage > 0 ? (yearStats.totalEconomicDamage / 1e9).toFixed(1) + 'B' : '0'}
          </div>
          <div className="text-sm opacity-60 mt-1">Economic Damage</div>
          {yearStats.totalReconstructionCost > 0 && (
            <div className="text-xs text-blue-500 mt-1">${(yearStats.totalReconstructionCost / 1e9).toFixed(1)}B reconstruction</div>
          )}
        </div>
        <div className="card-minimal text-center">
          <div className="text-xl font-mono font-light text-purple-600">{yearStats.responseRate}%</div>
          <div className="text-sm opacity-60 mt-1">OFDA Response Rate</div>
          <div className="text-xs text-purple-500 mt-1">{yearStats.appealRate}% appeals, {yearStats.declarationRate}% declared</div>
        </div>
        <div className="card-minimal text-center">
          <div className="text-xl font-mono font-light text-blue-600">{yearStats.avgDuration}</div>
          <div className="text-sm opacity-60 mt-1">Avg Duration (months)</div>
        </div>
        <div className="card-minimal text-center">
          <div className="text-lg font-sans font-light text-slate-700">
            {yearStats.deadliestDisaster.disaster_type || 'N/A'}
          </div>
          <div className="text-sm opacity-60 mt-1">Deadliest Event</div>
          {yearStats.deadliestDisaster.total_deaths > 0 && (
            <div className="text-xs text-red-500 mt-1">{yearStats.deadliestDisaster.total_deaths.toLocaleString()} deaths</div>
          )}
        </div>
      </div>

      {/* Mapbox Map */}
      <div className="card-minimal">
        <div className="h-96 relative">
          <Map
            ref={mapRef}
            {...viewport}
            onMove={evt => setViewport(evt.viewState)}
            mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
            mapStyle="mapbox://styles/mapbox/light-v11"
            style={{ width: '100%', height: '100%' }}
          >
            <NavigationControl position="top-right" />
            <FullscreenControl position="top-right" />
            <ScaleControl position="bottom-left" />

            {filteredDisasters.map((disaster) => {
              const markerElement = createDisasterIcon(disaster.disaster_type, disaster.severity_level);
              return (
                <Marker
                  key={disaster.id}
                  latitude={disaster.latitude}
                  longitude={disaster.longitude}
                  anchor="center"
                  onClick={(evt) => onMarkerClick(evt, disaster)}
                >
                  <div dangerouslySetInnerHTML={{ __html: markerElement.innerHTML }} />
                </Marker>
              );
            })}

            {popupInfo && (
              <Popup
                latitude={popupInfo.latitude}
                longitude={popupInfo.longitude}
                anchor="top"
                onClose={() => setPopupInfo(null)}
                closeOnClick={false}
                className="disaster-popup"
              >
                <div className="p-4 min-w-80 max-w-96 max-h-96 overflow-y-auto">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="text-lg">
                      {popupInfo.disaster_type === 'Flood' ? '🌊' :
                       popupInfo.disaster_type === 'Storm' ? '🌪️' :
                       popupInfo.disaster_type === 'Earthquake' ? '🌍' :
                       popupInfo.disaster_type === 'Drought' ? '🌵' :
                       popupInfo.disaster_type === 'Epidemic' ? '🏥' :
                       popupInfo.disaster_type.includes('Fire') ? '🔥' : '⚠️'}
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">{popupInfo.disaster_type}</h4>
                      <div className="text-xs text-gray-500">{popupInfo.disaster_subtype}</div>
                    </div>
                  </div>

                  <div className="space-y-3 text-xs">
                    {/* Basic Info */}
                    <div className="bg-gray-50 p-2 rounded">
                      <div className="font-medium text-gray-700 mb-1">Basic Information</div>
                      <div className="space-y-1">
                        <div><strong>Event ID:</strong> {popupInfo.dis_no}</div>
                        <div><strong>Date:</strong> {popupInfo.start_year}-{String(popupInfo.start_month || 1).padStart(2, '0')}-{String(popupInfo.start_day || 1).padStart(2, '0')}</div>
                        {popupInfo.duration_months && (
                          <div><strong>Duration:</strong> {popupInfo.duration_months} months</div>
                        )}
                        <div><strong>Country:</strong> {popupInfo.country} ({popupInfo.iso})</div>
                        <div><strong>Region:</strong> {popupInfo.subregion}, {popupInfo.region}</div>
                      </div>
                    </div>

                    {/* Location Details */}
                    {popupInfo.location && (
                      <div className="bg-blue-50 p-2 rounded">
                        <div className="font-medium text-blue-700 mb-1">Location Details</div>
                        <div className="text-blue-900">{popupInfo.location}</div>
                        {popupInfo.origin && (
                          <div className="mt-1"><strong>Origin:</strong> {popupInfo.origin}</div>
                        )}
                      </div>
                    )}

                    {/* Human Impact */}
                    {(popupInfo.total_deaths > 0 || popupInfo.total_affected > 0 || popupInfo.no_injured > 0 || popupInfo.no_homeless > 0) && (
                      <div className="bg-red-50 p-2 rounded">
                        <div className="font-medium text-red-700 mb-1">Human Impact</div>
                        <div className="space-y-1">
                          {popupInfo.total_deaths > 0 && (
                            <div><strong>Deaths:</strong> <span className="text-red-600">{popupInfo.total_deaths.toLocaleString()}</span></div>
                          )}
                          {popupInfo.no_injured > 0 && (
                            <div><strong>Injured:</strong> <span className="text-orange-600">{popupInfo.no_injured.toLocaleString()}</span></div>
                          )}
                          {popupInfo.total_affected > 0 && (
                            <div><strong>Total Affected:</strong> <span className="text-amber-600">{popupInfo.total_affected.toLocaleString()}</span></div>
                          )}
                          {popupInfo.no_homeless > 0 && (
                            <div><strong>Homeless:</strong> {popupInfo.no_homeless.toLocaleString()}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Economic Impact */}
                    {(popupInfo.total_damage > 0 || popupInfo.reconstruction_costs > 0 || popupInfo.insured_damage > 0) && (
                      <div className="bg-green-50 p-2 rounded">
                        <div className="font-medium text-green-700 mb-1">Economic Impact</div>
                        <div className="space-y-1">
                          {popupInfo.total_damage > 0 && (
                            <div><strong>Total Damage:</strong> ${(popupInfo.total_damage_adj || popupInfo.total_damage).toLocaleString()}</div>
                          )}
                          {popupInfo.reconstruction_costs > 0 && (
                            <div><strong>Reconstruction:</strong> ${(popupInfo.reconstruction_costs_adj || popupInfo.reconstruction_costs).toLocaleString()}</div>
                          )}
                          {popupInfo.insured_damage > 0 && (
                            <div><strong>Insured Damage:</strong> ${(popupInfo.insured_damage_adj || popupInfo.insured_damage).toLocaleString()}</div>
                          )}
                          {popupInfo.economic_impact_per_person && (
                            <div><strong>Impact per Person:</strong> ${popupInfo.economic_impact_per_person.toFixed(2)}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Response & Aid */}
                    {(popupInfo.ofda_response === 'Yes' || popupInfo.appeal === 'Yes' || popupInfo.declaration === 'Yes' || popupInfo.aid_contribution > 0) && (
                      <div className="bg-purple-50 p-2 rounded">
                        <div className="font-medium text-purple-700 mb-1">Response & Aid</div>
                        <div className="space-y-1">
                          {popupInfo.ofda_response === 'Yes' && (
                            <div><span className="text-green-600">✓</span> OFDA Response</div>
                          )}
                          {popupInfo.appeal === 'Yes' && (
                            <div><span className="text-green-600">✓</span> International Appeal</div>
                          )}
                          {popupInfo.declaration === 'Yes' && (
                            <div><span className="text-green-600">✓</span> Disaster Declaration</div>
                          )}
                          {popupInfo.aid_contribution > 0 && (
                            <div><strong>Aid Contribution:</strong> ${popupInfo.aid_contribution.toLocaleString()}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Additional Details */}
                    {(popupInfo.magnitude || popupInfo.associated_types || popupInfo.event_name) && (
                      <div className="bg-gray-50 p-2 rounded">
                        <div className="font-medium text-gray-700 mb-1">Additional Details</div>
                        <div className="space-y-1">
                          {popupInfo.event_name && (
                            <div><strong>Event Name:</strong> {popupInfo.event_name}</div>
                          )}
                          {popupInfo.magnitude && (
                            <div><strong>Magnitude:</strong> {popupInfo.magnitude} {popupInfo.magnitude_scale && `(${popupInfo.magnitude_scale})`}</div>
                          )}
                          {popupInfo.associated_types && (
                            <div><strong>Associated Types:</strong> {popupInfo.associated_types}</div>
                          )}
                          {popupInfo.classification_key && (
                            <div><strong>Classification:</strong> {popupInfo.classification_key}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Data Source */}
                    <div className="text-center text-xs text-gray-400 pt-2 border-t">
                      <div>Source: EM-DAT • Last Updated: {popupInfo.last_update}</div>
                      {popupInfo.external_ids && (
                        <div>External ID: {popupInfo.external_ids}</div>
                      )}
                    </div>
                  </div>
                </div>
              </Popup>
            )}
          </Map>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm opacity-60">
            Showing {filteredDisasters.length} disaster events for {selectedYear}
            {selectedDisasterType !== 'All' && ` (${selectedDisasterType})`}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-600 rounded-full"></div>
              <span>High Impact</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
              <span>Medium Impact</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>Low Impact</span>
            </div>
          </div>
        </div>
      </div>

      {/* Disaster List for Current Year */}
      {filteredDisasters.length > 0 && (
        <div className="card-minimal mt-6">
          <h3 className="text-lg font-serif font-light mb-4">
            Disasters in {selectedYear}
            {selectedDisasterType !== 'All' && ` - ${selectedDisasterType}`}
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {filteredDisasters.map((disaster) => (
              <div key={disaster.id} className="flex items-center justify-between py-2 px-3 bg-stone-50 hover:bg-stone-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="text-lg">
                    {disaster.disaster_type === 'Flood' ? '🌊' :
                     disaster.disaster_type === 'Storm' ? '🌪️' :
                     disaster.disaster_type === 'Earthquake' ? '🌍' :
                     disaster.disaster_type === 'Drought' ? '🌵' :
                     disaster.disaster_type === 'Epidemic' ? '🏥' :
                     disaster.disaster_type.includes('Fire') ? '🔥' : '⚠️'}
                  </div>
                  <div>
                    <div className="font-medium text-sm">{disaster.disaster_type}</div>
                    <div className="text-xs opacity-60">{disaster.country}</div>
                  </div>
                </div>
                <div className="text-right text-sm">
                  {disaster.total_deaths > 0 && (
                    <div className="text-red-600">{disaster.total_deaths.toLocaleString()} deaths</div>
                  )}
                  {disaster.total_affected > 0 && (
                    <div className="text-amber-600 text-xs">{disaster.total_affected.toLocaleString()} affected</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricalTimelineAnalysisMapbox;